import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  globalConfig,
  getOrderDetailsByIdApi,
} from "../../services/api";
import * as PR from "../../prime-modules";
import {
  checkDataSize,
  displayBase64,
  esimActivateUrl,
  formattedDate,
} from "../../utils/reuse";
import { isIOS, isAndroid, isMobile } from "react-device-detect";
import moment from "moment";
import "./EsimDetailsByOrderId.scss";

const EsimDetailsByOrderId = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [esimDetail, setEsimDetail] = useState(null);

  const { isLoading, error } = useQuery(
    ["getOrderDetailsById", orderId],
    () => getOrderDetailsByIdApi(orderId),
    {
      enabled: !!orderId,
      onSuccess: (data) => setEsimDetail(data),
    },
  );

  if (isLoading) {
    return (
      <div className="main-section">
        <div className="loading-container">
          <i className="pi pi-spin pi-spinner"></i>
          <p>Loading details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-section">
        <div className="loading-container">
          <i
            className="pi pi-exclamation-triangle"
            style={{ color: "#ffb300" }}
          ></i>
          <p>{error.message || "Error fetching eSIM details"}</p>
          <PR.Button
            label="Go Back"
            onClick={() => navigate(-1)}
            className="mt-3"
          />
        </div>
      </div>
    );
  }

  const simStatus = esimDetail?.simStatus
  const isReleased = simStatus === "Released" || simStatus === "Downloaded";
  const smdpAddress = esimDetail?.smdpAddress;
  const matchingId = esimDetail?.matchingId;

  // Remaining days calculation
  let daysRemaining = 0;
  if (esimDetail?.finishedAt) {
    let expiryDate = moment(esimDetail?.finishedAt);
    daysRemaining = Math.max(0, expiryDate.diff(moment(), "days"));
  }

  const downloadQRCode = () => {
    const link = document.createElement("a");
    link.href = displayBase64(esimDetail?.qrCode);
    link.download = `eSIM_QR_${orderId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeBundle = esimDetail;
  const bundleName = activeBundle?.bundleDescription;
  const totalData = esimDetail?.dataAmountBytes;
  const remainingData = esimDetail?.dataAmountRemaining;
  const progressBarValue = totalData
    ? ((remainingData / totalData) * 100).toFixed(0)
    : 0;


  if (!esimDetail && !isLoading && !error) {
    return (
      <div className="main-section">
        <div className="loading-container">
          <i className="pi pi-info-circle" style={{ color: "#2196f3" }}></i>
          <p>No eSIM found for this order.</p>
          <PR.Button
            label="Go Back"
            onClick={() => navigate(-1)}
            className="mt-3"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="main-section">
      <section className="esim-details-order-section">
        <div className="header-section text-center">
          <h1>eSIM Details</h1>
          <p>Order ID: {orderId}</p>
        </div>

        <div className="main-grid">
          {/* Status Section */}
          <div className="card qr-block">
            {isReleased ? (
              <div className="qr-section flex flex-column align-items-center gap-4">
                <h2 className="text-center m-0">Install eSIM</h2>
                <div className="activation-links mb-0">
                  <div className="flex align-items-center gap-3 m-flex-column">
                    <div
                      className="qr-wrapper"
                    >
                      <PR.Image
                        src={displayBase64(esimDetail?.qrCode)}
                        alt="eSIM QR Code"
                        className="qr-image visible"
                        width="125"
                      />
                    </div>
                    <div className="flex flex-column gap-3">
                      {(isIOS || !isMobile) && <a
                        href={esimActivateUrl({ smdpAddress, matchingId })}
                        className="activation-btn"
                      >
                        {!isMobile && <i className="pi pi-apple"></i>}
                        {isMobile ? "Activate eSIM on this Device" : "Install on iOS"}
                      </a>}
                      {(isAndroid || !isMobile) && <a
                        href={esimActivateUrl({ smdpAddress, matchingId })}
                        className="activation-btn"
                      >
                        {!isMobile && <i className="pi pi-android"></i>}
                        {isMobile ? "Activate eSIM on this Device" : "Install on Android"}
                      </a>}
                    </div>
                  </div>
                </div>

                <div className="qr-actions">
                  <p>
                    You can scan this QR code right away from the target phone
                    to activate your bundle
                  </p>
                  <PR.Button
                    label="Download QR Code"
                    icon="pi pi-download"
                    className="download-btn"
                    onClick={downloadQRCode}
                  />
                </div>
              </div>
            ) : (
              <div className="usage-section">
                <h2 className="text-center mb-4">eSIM Details</h2>
                <div className="usage-item">
                  <div className="usage-labels">
                    <span>
                      {checkDataSize(totalData - remainingData)}{" "}
                      used
                    </span>
                    <span>
                      {esimDetail?.isUnlimited
                        ? "Unlimited"
                        : `${checkDataSize(remainingData)} of ${checkDataSize(totalData)} remaining`}
                    </span>
                  </div>
                  <PR.ProgressBar value={progressBarValue} showValue={false} />
                </div>

                  {daysRemaining ? (
                    <div className="expiry-info">
                      <i className="pi pi-calendar-times"></i>
                      <div className="info-text">
                        <h4>{daysRemaining} Days remaining</h4>
                        <p>
                          Expires on: {formattedDate(esimDetail?.finishedAt)}
                        </p>
                      </div>
                    </div>
                ) : null}

                <div className="mt-4">
                  <p>
                    <strong>ICCID:</strong> {esimDetail?.iccid}
                  </p>
                  <p>
                    <strong>Bundle:</strong> {bundleName || "N/A"}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <span className="status-span">{simStatus}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
          {/* App Promotion */}
          <div className="app-promo-section">
            <div className="wrapper">
              <img src="/data2go/logos/data2go_logo.svg" className="mb-5" />
              <h2>Get our app!</h2>
              <p>
                Our mobile app offers robust, secure and adaptable mobile data
                solutions tailored for the discerning traveller of today.
              </p>
              <div className="app-links">
                <a
                  href={globalConfig?.appStoreLink}
                  target="_blank"
                  rel="noreferrer"
                  className="app-links-btn"
                >
                  <i className="pi pi-apple"></i>
                  App Store
                </a>
                <a
                  href={globalConfig?.playStoreLink}
                  target="_blank"
                  rel="noreferrer"
                  className="app-links-btn"
                >
                  <i className="pi pi-google"></i>
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="installation-instructions mt-4">
          <PR.Accordion activeIndex={0}>
            {(isIOS || !isMobile) && (
              <PR.AccordionTab header="How to Install on iOS">
                <div className="steps-list">
                  <p>
                    <strong>Option 1: Direct Activation</strong>
                  </p>
                  <ol>
                    <li>Tap the "Install on iOS" button above.</li>
                    <li>
                      A prompt will appear to "Install Cellular Plan" - tap{" "}
                      <strong>Continue</strong>.
                    </li>
                    <li>Follow the on-screen instructions to finish.</li>
                  </ol>
                  <p className="mt-3">
                    <strong>Option 2: QR Code Scan</strong>
                  </p>
                  <ol>
                    <li>
                      Go to <strong>Settings</strong> {">"}{" "}
                      <strong>Cellular</strong>.
                    </li>
                    <li>
                      Tap <strong>Add eSIM</strong>.
                    </li>
                    <li>
                      Select <strong>Use QR Code</strong> and scan the code
                      above.
                    </li>
                  </ol>
                </div>
              </PR.AccordionTab>
            )}
            {(isAndroid || !isMobile) && (
              <PR.AccordionTab header="How to Install on Android">
                <div className="steps-list">
                  <p>
                    <strong>Option 1: Direct Activation</strong>
                  </p>
                  <ol>
                    <li>Tap the "Install on Android" button above.</li>
                    <li>
                      Your device will automatically open the eSIM set up
                      screen.
                    </li>
                    <li>Follow the prompts to download and install.</li>
                  </ol>
                  <p className="mt-3">
                    <strong>Option 2: QR Code Scan</strong>
                  </p>
                  <ol>
                    <li>
                      Go to <strong>Settings</strong> {">"}{" "}
                      <strong>Network & Internet</strong>.
                    </li>
                    <li>
                      Tap <strong>+</strong> next to SIMs.
                    </li>
                    <li>
                      Tap <strong>Download a SIM instead?</strong> and scan the
                      QR code above.
                    </li>
                  </ol>
                </div>
              </PR.AccordionTab>
            )}
          </PR.Accordion>
        </div>
      </section>
    </div>
  );
};

export default EsimDetailsByOrderId;
